import * as React from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce/lib';
import { useTranslation } from 'react-i18next';
import { IOverviewProps } from './OverviewContainer';
import { getRequestForTimeFrame } from 'helpers/date';
import { TimeFrame } from 'models/ReportData';
import { OverviewViewMode, UserSystemGroup, UserSystemGroupStrings } from 'store/storeTypes';
import { isDateInCurrentSelectedRange, useInterval } from 'helpers/intervalUtils';

const OverviewMobile = React.lazy(() => import('./OverviewMobile'));
const OverviewDesktop = React.lazy(() => import('./OverviewDesktop'));

const Overview = memo((props: IOverviewProps) => {
  const { t } = useTranslation();
  const {
    overviewAchievementsRequest,
    overviewImpressionsAvailabilityCardsRequest,
    overviewProductionRateRequest,
    overviewProductivityRequest,
    overviewImpressionsAvailabilityChartsRequest,
    overviewSpeedChartRequest,
    overviewUtilizationChartRequest,
    overviewReportMainRequest,
    overviewReportAvailabilityRequest,
    getMaxKeepAliveRequest,
    timeFrame,
    selectedSystemGroup,
    day,
    selectedSystemsRequest,
    screenWidth,
    screenHeight,
    pbiReportHeight,
    pbiReportWidth,
    customTimeframeProperties,
    isMachineFilterOpen,
    endDate,
    refreshTime,
    timeViewMode,
    isTimeViewModeResolved,
  } = props;
  //*Production Hours mode is in scope only for DTG/Apollo, DTF is unaffected
  const isProductionMode =
    timeViewMode === OverviewViewMode.Production && selectedSystemGroup !== UserSystemGroup.DTF;

  const { startInterval, clearExistingInterval } = useInterval();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (pbiReportWidth < 1366 || pbiReportHeight < 567) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [pbiReportHeight, pbiReportWidth, screenWidth, screenHeight]);

  const [debImpressionsAvailabilityCardsRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean) => {
      return overviewImpressionsAvailabilityCardsRequest(args);
    },
    1
  );
  const [debProductionRateRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean) => {
      return overviewProductionRateRequest(args);
    },
    1
  );
  const [debProductivityRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean) => {
      return overviewProductivityRequest(args);
    },
    1
  );

  const [debImpressionsAvailabilityChartsRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean) => {
      return overviewImpressionsAvailabilityChartsRequest(args, isSilent);
    },
    1
  );

  const [debSpeedChartRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean) => {
      return overviewSpeedChartRequest(args);
    },
    1
  );

  const [debUtilizationChartRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean) => {
      return overviewUtilizationChartRequest(args);
    },
    1
  );

  const [debAchievementsRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean, isRealTimeRequest = false) => {
      return overviewAchievementsRequest(args, isSilent, isRealTimeRequest);
    },
    1
  );
  const [debMaxKeepAliveRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean, isRealTimeRequest = false) => {
      return getMaxKeepAliveRequest(args, isSilent, isRealTimeRequest);
    },
    1
  );
  const [debOverviewReportMainRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean, isRealTimeRequest = false) => {
      return overviewReportMainRequest(args, isSilent, isRealTimeRequest);
    },
    1
  );
  const [debOverviewReportAvailabilityRequest] = useDebouncedCallback(
    (args: Record<string, unknown>, isSilent: boolean, isRealTimeRequest = false) => {
      return overviewReportAvailabilityRequest(args, isSilent, isRealTimeRequest);
    },
    1
  );

  const getData = (
    day: Date,
    timeFrame: TimeFrame,
    isSilent = false,
    selectedSystemGroup?: UserSystemGroupStrings
  ) => {
    const filterByPage = getRequestForTimeFrame(day, timeFrame, customTimeframeProperties);
    if (selectedSystemsRequest) {
      filterByPage['systems'] = selectedSystemsRequest;
    }
    filterByPage['systemGroup'] = selectedSystemGroup;
    filterByPage['isProductionHours'] = isProductionMode;
    //* by new business logic there will be another requests for Apollo
    if (selectedSystemGroup === UserSystemGroup.Apollo) {
      //*requests for Apollo
      debMaxKeepAliveRequest(filterByPage, isSilent);
      debOverviewReportMainRequest(filterByPage, isSilent);
      debOverviewReportAvailabilityRequest(filterByPage, isSilent);
      debAchievementsRequest(filterByPage, isSilent);
    } else {
      //*for DTF and DTG
      debAchievementsRequest(filterByPage, isSilent);
      debImpressionsAvailabilityCardsRequest(filterByPage, isSilent);
      debProductionRateRequest(filterByPage, isSilent);
      debImpressionsAvailabilityChartsRequest(filterByPage, isSilent);
      debSpeedChartRequest(filterByPage, isSilent);
      debUtilizationChartRequest(filterByPage, isSilent);
      selectedSystemGroup === 'DTG' && debProductivityRequest(filterByPage, isSilent);
    }
  };

  //*this useEffect is special only for apollo group - because we have completely another async logic for this group
  const isTodayDateInCurrentSelectedRange = isDateInCurrentSelectedRange({
    selectedDay: day,
    timeFrame,
    customTimeframeProperties,
    endDate,
  });

  //this useEffect is used only for apollo and dtg group
  useEffect(() => {
    if (selectedSystemGroup === UserSystemGroup.DTF || !isTimeViewModeResolved) {
      return;
    }

    const filterByPage = getRequestForTimeFrame(day, timeFrame, customTimeframeProperties);
    filterByPage['systemGroup'] = selectedSystemGroup;
    filterByPage['isProductionHours'] = isProductionMode;
    if (selectedSystemsRequest) {
      filterByPage['systems'] = selectedSystemsRequest;
    }

    clearExistingInterval();

    // Call immediately and set up the interval if the condition is true
    if (isTodayDateInCurrentSelectedRange) {
      startInterval(
        filterByPage,
        refreshTime,
        debMaxKeepAliveRequest,
        debOverviewReportMainRequest,
        debOverviewReportAvailabilityRequest,
        debAchievementsRequest
      );
    } else {
      debMaxKeepAliveRequest(filterByPage, false);
      debOverviewReportMainRequest(filterByPage, false);
      debOverviewReportAvailabilityRequest(filterByPage, false);
      debAchievementsRequest(filterByPage, false);
    }

    return () => {
      clearExistingInterval();
    };
  }, [
    timeFrame,
    day,
    selectedSystemsRequest,
    selectedSystemGroup,
    customTimeframeProperties,
    isTodayDateInCurrentSelectedRange,
    isProductionMode,
    isTimeViewModeResolved,
    startInterval,
    clearExistingInterval,
    debMaxKeepAliveRequest,
    debOverviewReportMainRequest,
    debOverviewReportAvailabilityRequest,
    debAchievementsRequest,
  ]);

  //*the real-time interval above always kicks off its immediate request as silent (no spinner, see useInterval),
  //*which is fine for background refreshes but not when the user explicitly toggles 24h/Production - force a visible refetch for that case.
  //*runs after the effect above in the same commit, so its non-silent call is the one the debounce actually executes.
  const isProductionModeRef = useRef(isProductionMode);
  useEffect(() => {
    const hasProductionModeChanged = isProductionModeRef.current !== isProductionMode;
    isProductionModeRef.current = isProductionMode;
    if (!hasProductionModeChanged || !isTimeViewModeResolved) {
      return;
    }
    if (selectedSystemGroup === UserSystemGroup.DTF || !isTodayDateInCurrentSelectedRange) {
      return;
    }

    const filterByPage = getRequestForTimeFrame(day, timeFrame, customTimeframeProperties);
    filterByPage['systemGroup'] = selectedSystemGroup;
    filterByPage['isProductionHours'] = isProductionMode;
    if (selectedSystemsRequest) {
      filterByPage['systems'] = selectedSystemsRequest;
    }

    debMaxKeepAliveRequest(filterByPage, false);
    debOverviewReportMainRequest(filterByPage, false);
    debOverviewReportAvailabilityRequest(filterByPage, false);
    debAchievementsRequest(filterByPage, false);
  }, [isProductionMode]);

  //*only for dtf
  useEffect(() => {
    if (selectedSystemGroup !== UserSystemGroup.DTF) {
      return;
    }
    if (timeFrame) {
      getData(day, timeFrame, false, selectedSystemGroup);
    }
  }, [
    timeFrame,
    day,
    selectedSystemsRequest,
    selectedSystemGroup,
    customTimeframeProperties,
    isProductionMode,
  ]);

  return (
    <>
      {isMobile ? (
        <OverviewMobile {...props} isProductionMode={isProductionMode} />
      ) : (
        <OverviewDesktop {...props} isProductionMode={isProductionMode} />
      )}
    </>
  );
});

export default Overview;
